import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    MenuItem,
    CircularProgress
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import {
    DetalharDadosAdicionais,
    IDetalheProdutoEstoque
} from "../../../../../../../types/api/estoque/detalhar-produto-estoque";
import axiosClient from "../../../../../../../lib/axios";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { EditarDadosAdicionaisPayload } from "../../../../../../../types/mutations/estoque/editar-produtos";
import SelectLoja from "../../../../../../../components/SelectsSimpleApiQuery/SelectLoja";
import SelectAnpGrupo from "../../../../../../../components/SelectsSimpleApiQuery/SelectAnpGrupo";
import SelectAgrotoxico from "../../../../../../../components/SelectsSimpleApiQuery/SelectAgrotoxico";

type Props = {
    open: boolean;
    onClose: () => void;
    produto: IDetalheProdutoEstoque;
};

export default function DialogEditDadosAdicionais({ open, onClose, produto }: Props) {
    const { handleSubmit, control, formState: { errors }, watch } = useForm<DetalharDadosAdicionais>({
        defaultValues: {
            pode_comprar: produto.pode_comprar ?? "",
            desativado: produto.desativado ?? "",
            produto_formula: produto.produto_formula ?? "",
            transfere: produto.transfere ?? "",
            codigo_anterior: produto.codigo_anterior ?? "",
            tipo_item: produto.tipo_item ?? "",
            familia: produto.familia ?? "",
            tipo_produto_codigo: produto.tipo_produto_codigo ?? "",
            codigo_cd: produto.codigo_cd ?? null,
            financiado: produto.financiado ?? "",
            atualiza_preco: produto.atualiza_preco ?? "N",
            preco_quatro_decimais: produto.preco_quatro_decimais ?? "N",
            perc_variacao_compra: produto.perc_variacao_compra ?? "",
            pede_valor_total_nf: produto.pede_valor_total_nf ?? "N",
            estorno_icms: produto.estorno_icms ?? "N",
            emite_receita: produto.emite_receita ?? "N",
            gera_lmc: produto.gera_lmc ?? "N",
            grupo_anp: produto.grupo_anp ?? null,
            registro_agricola: produto.registro_agricola ?? null,
            classificacao: produto.classificacao ?? "",
        }
    });

    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const geraLMC = watch("gera_lmc");

    const { mutate, isLoading } = useMutation({
        mutationKey: ['editar-produto', produto.id],
        mutationFn: (payload: EditarDadosAdicionaisPayload) =>
            axiosClient.put(`/estoque/produtos/editar-produto/${produto.id}`, payload),
        onSuccess: () => {
            enqueueSnackbar("Produto alterado com sucesso!", { variant: "success" });
            queryClient.invalidateQueries("detalhe-produto");
            onClose();
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || "Erro ao editar produto!", { variant: "error" });
        }
    });

    const onSubmit = (values: DetalharDadosAdicionais) => {
        mutate({
            ...values,
            codigo_cd: values.codigo_cd?.id,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Editar Dados Adicionais</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} py={1}>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="pode_comprar"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    select
                                    label="Pede p/comprar"
                                    fullWidth
                                    {...field}
                                    value={field.value ?? ""}
                                    error={!!errors.pode_comprar}
                                    helperText={errors.pode_comprar?.message}
                                >
                                    <MenuItem value="S">Sim</MenuItem>
                                    <MenuItem value="N">Não</MenuItem>
                                </TextField>
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="desativado"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    select
                                    label="Desativado"
                                    fullWidth
                                    {...field}
                                    value={field.value ?? ""}
                                    error={!!errors.desativado}
                                    helperText={errors.desativado?.message}
                                >
                                    <MenuItem value="S">Sim</MenuItem>
                                    <MenuItem value="N">Não</MenuItem>
                                </TextField>
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="produto_formula"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    select
                                    label="Produto Fórmula"
                                    fullWidth
                                    {...field}
                                    value={field.value ?? ""}
                                    error={!!errors.produto_formula}
                                    helperText={errors.produto_formula?.message}
                                >
                                    <MenuItem value="S">Sim</MenuItem>
                                    <MenuItem value="N">Não</MenuItem>
                                </TextField>
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="transfere"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    select
                                    label="Ressupre"
                                    fullWidth
                                    {...field}
                                    value={field.value ?? ""}
                                    error={!!errors.transfere}
                                    helperText={errors.transfere?.message}
                                >
                                    <MenuItem value="S">Sim</MenuItem>
                                    <MenuItem value="N">Não</MenuItem>
                                </TextField>
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="codigo_anterior"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Código Anterior"
                                    fullWidth
                                    error={!!errors.codigo_anterior}
                                    helperText={errors.codigo_anterior?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="tipo_item"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Tipo Item"
                                    fullWidth
                                    error={!!errors.tipo_item}
                                    helperText={errors.tipo_item?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="familia"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Família"
                                    fullWidth
                                    error={!!errors.familia}
                                    helperText={errors.familia?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="tipo_produto_codigo"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Tipo Produto"
                                    fullWidth
                                    error={!!errors.tipo_produto_codigo}
                                    helperText={errors.tipo_produto_codigo?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="codigo_cd"
                            control={control}
                            render={({ field }) => (
                                <SelectLoja
                                    label="Loja Centro Distribuição"
                                    possuiBtnLimpar
                                    value={field.value ?? null}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="financiado"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Financiamento (Código)"
                                    type="number"
                                    fullWidth
                                    slotProps={{ htmlInput: { min: "0", step: "1" } }}
                                    error={!!errors.financiado}
                                    helperText={errors.financiado?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="atualiza_preco"
                            control={control}
                            rules={{ required: "Campo obrigatório" }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    select
                                    label="Atual. Preço Automático"
                                    fullWidth
                                    required
                                    error={!!errors.atualiza_preco}
                                    helperText={errors.atualiza_preco?.message}
                                >
                                    <MenuItem value="S">Sim</MenuItem>
                                    <MenuItem value="N">Não</MenuItem>
                                </TextField>
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="preco_quatro_decimais"
                            control={control}
                            rules={{ required: "Campo obrigatório" }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    select
                                    label="Pr. Venda c/ 4 Decimais"
                                    fullWidth
                                    required
                                    error={!!errors.preco_quatro_decimais}
                                    helperText={errors.preco_quatro_decimais?.message}
                                >
                                    <MenuItem value="S">Sim</MenuItem>
                                    <MenuItem value="N">Não</MenuItem>
                                </TextField>
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="perc_variacao_compra"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="% Variação Compra"
                                    type="number"
                                    fullWidth
                                    slotProps={{ htmlInput: { step: "0.1", min: "0", max: "999" } }}
                                    error={!!errors.perc_variacao_compra}
                                    helperText={errors.perc_variacao_compra?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="pede_valor_total_nf"
                            control={control}
                            rules={{ required: "Campo obrigatório" }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    select
                                    label="Pede Vr. Total NF"
                                    fullWidth
                                    required
                                    error={!!errors.pede_valor_total_nf}
                                    helperText={errors.pede_valor_total_nf?.message}
                                >
                                    <MenuItem value="S">Sim</MenuItem>
                                    <MenuItem value="N">Não</MenuItem>
                                </TextField>
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="emite_receita"
                            control={control}
                            rules={{ required: "Campo obrigatório" }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    select
                                    label="Emite Receita Agp"
                                    fullWidth
                                    required
                                    error={!!errors.emite_receita}
                                    helperText={errors.emite_receita?.message}
                                >
                                    <MenuItem value="S">Sim</MenuItem>
                                    <MenuItem value="N">Não</MenuItem>
                                </TextField>
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="gera_lmc"
                            control={control}
                            rules={{ required: "Campo obrigatório" }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    select
                                    label="Gera LMC"
                                    fullWidth
                                    required
                                    error={!!errors.gera_lmc}
                                    helperText={errors.gera_lmc?.message}
                                >
                                    <MenuItem value="S">Sim</MenuItem>
                                    <MenuItem value="N">Não</MenuItem>
                                </TextField>
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="grupo_anp"
                            control={control}
                            rules={{
                                validate: (value) => {
                                    if (geraLMC === "S") {
                                        if (!value) {
                                            return "Grupo ANP é obrigatório";
                                        }
                                    }
                                    return true;
                                }
                            }}
                            render={({ field }) => (
                                <SelectAnpGrupo
                                    value={field.value ? [field.value] : null}
                                    onChange={field.onChange}
                                    required={geraLMC === "S"}
                                    error={!!errors.grupo_anp}
                                    multiple={false}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="registro_agricola"
                            control={control}
                            render={({ field }) => (
                                <SelectAgrotoxico
                                    value={Array.isArray(field.value) ? field.value : field.value ? [field.value] : null}
                                    onChange={field.onChange}
                                    label="Registro no Ministério da Agricultura"
                                    unique_select
                                    error={!!errors.registro_agricola}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            name="classificacao"
                            control={control}
                            rules={{ required: "Campo obrigatório" }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    select
                                    label="Classif. Origem"
                                    fullWidth
                                    required
                                    error={!!errors.classificacao}
                                    helperText={errors.classificacao?.message}
                                >
                                    <MenuItem value="G">Genuíno</MenuItem>
                                    <MenuItem value="O">Original</MenuItem>
                                    <MenuItem value="P">Paralelo</MenuItem>
                                    <MenuItem value="U">Outros</MenuItem>
                                </TextField>
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>Cancelar</Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                    onClick={handleSubmit(onSubmit)}
                >
                    {isLoading && (
                        <CircularProgress size={15} sx={{ marginRight: 1 }} />
                    )}
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
}